import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { services } from '../mock';
import { Phone, Mail, MapPin, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    city: '',
    message: ''
  });
  
  // État des erreurs et des champs touchés
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validation des champs
  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Le nom est requis';
        if (value.length < 2) return 'Le nom doit contenir au moins 2 caractères';
        return '';
      case 'email':
        if (!value.trim()) return 'L\'email est requis';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Veuillez entrer un email valide';
        return '';
      case 'phone':
        if (!value.trim()) return 'Le téléphone est requis';
        if (!/^(0[1-9])[\s.-]?([0-9]{2}[\s.-]?){4}$/.test(value.replace(/\s/g, ''))) {
          return 'Veuillez entrer un numéro valide (ex: 06 12 34 56 78)';
        }
        return '';
      case 'service':
        if (!value) return 'Veuillez sélectionner un service';
        return '';
      case 'city':
        if (!value.trim()) return 'La ville est requise';
        return '';
      default:
        return '';
    }
  };

  // Gestion du blur
  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const error = validateField(field, formData[field]);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  // Gestion du changement
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (touched[field]) {
      const error = validateField(field, value);
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Valider tous les champs requis
    const newErrors = {};
    const fieldsToValidate = ['name', 'email', 'phone', 'service', 'city'];
    
    fieldsToValidate.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) newErrors[field] = error;
    });
    
    setErrors(newErrors);
    setTouched(fieldsToValidate.reduce((acc, field) => ({ ...acc, [field]: true }), {}));
    
    if (Object.keys(newErrors).length > 0) {
      toast.error('Veuillez corriger les erreurs du formulaire');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de l\'envoi');
      }
      
      toast.success('Demande envoyée ! Nous vous recontacterons dans les plus brefs délais.');
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        city: '',
        message: ''
      });
      setTouched({});
      setErrors({});
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Composant pour afficher les erreurs
  const ErrorMessage = ({ error }) => {
    if (!error) return null;
    return (
      <div className="flex items-center gap-1 mt-1 text-red-500 text-sm">
        <AlertCircle className="h-4 w-4" />
        <span>{error}</span>
      </div>
    );
  };

  return (
    <section id="contact" className="py-24 bg-[#F4F7F6] font-['Poppins'] scroll-mt-40">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4 text-gray-800">
            Envie d'échanger sur vos besoins ?
          </h2>
          <p className="text-xl text-gray-600">
            Demandez votre devis gratuit et sans engagement
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <div className="bg-white rounded-3xl shadow-xl p-8">
            <h3 className="text-2xl font-bold mb-6 text-gray-800">Demande de devis</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Nom complet *</label>
                <Input 
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  onBlur={() => handleBlur('name')}
                  placeholder="Votre nom"
                  className={`rounded-xl ${errors.name && touched.name ? 'border-red-500 focus:ring-red-500' : ''}`}
                  data-testid="contact-name-input"
                />
                {touched.name && <ErrorMessage error={errors.name} />}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Email *</label>
                <Input 
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  onBlur={() => handleBlur('email')}
                  placeholder="votre@email.fr"
                  className={`rounded-xl ${errors.email && touched.email ? 'border-red-500 focus:ring-red-500' : ''}`}
                  data-testid="contact-email-input"
                />
                {touched.email && <ErrorMessage error={errors.email} />}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Téléphone *</label>
                <Input 
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  onBlur={() => handleBlur('phone')}
                  placeholder="06 12 34 56 78"
                  className={`rounded-xl ${errors.phone && touched.phone ? 'border-red-500 focus:ring-red-500' : ''}`}
                  data-testid="contact-phone-input"
                />
                {touched.phone && <ErrorMessage error={errors.phone} />}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Service souhaité *</label>
                <Select 
                  value={formData.service} 
                  onValueChange={(value) => {
                    handleChange('service', value);
                    if (!touched.service) setTouched(prev => ({ ...prev, service: true }));
                  }}
                >
                  <SelectTrigger 
                    className={`rounded-xl ${errors.service && touched.service ? 'border-red-500' : ''}`}
                    data-testid="contact-service-select"
                  >
                    <SelectValue placeholder="Sélectionner un service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map(service => (
                      <SelectItem key={service.id} value={service.id}>
                        {service.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {touched.service && <ErrorMessage error={errors.service} />}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Ville *</label>
                <Input 
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  onBlur={() => handleBlur('city')}
                  placeholder="Votre ville"
                  className={`rounded-xl ${errors.city && touched.city ? 'border-red-500 focus:ring-red-500' : ''}`}
                  data-testid="contact-city-input"
                />
                {touched.city && <ErrorMessage error={errors.city} />}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Message (optionnel)</label>
                <Textarea 
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  placeholder="Décrivez vos besoins..."
                  rows={4}
                  className="rounded-xl"
                  data-testid="contact-message-textarea"
                />
              </div>

              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full bg-[#0A2320] hover:bg-[#0d2e2a] text-white font-semibold rounded-xl" 
                size="lg"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
              </Button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-[#0A2320] to-[#153254] text-[#F8FAF9] rounded-3xl shadow-xl p-8">
              <h3 className="text-2xl font-bold mb-6 text-[#F8FAF9]">Contactez-nous</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="font-semibold">Téléphone</p>
                    <a href="tel:0981543842" className="hover:text-[#D4AF37] transition-colors block">09 81 54 38 42</a>
                    <p className="text-white/60 text-xs mt-1">Disponible aux horaires d'ouverture</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="font-semibold">Email</p>
                    <a href="mailto:tmc60110@gmail.com" className="hover:text-[#D4AF37] transition-colors">tmc60110@gmail.com</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="font-semibold">Zone d'intervention</p>
                    <p>Rayon de 50 km autour de Méru</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="font-semibold">Horaires</p>
                    <p className="text-sm">Lundi, Mardi, Jeudi, Vendredi : 9h-14h et 16h-19h</p>
                    <p className="text-sm">Mercredi et Samedi : 9h-16h <span className="text-white/60 text-xs">(journée continue)</span></p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl shadow-xl p-8">
              <h3 className="text-xl font-bold mb-4 text-gray-800">Pourquoi nous choisir ?</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#0A2320] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span>Réponse sous 24h</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#0A2320] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span>Devis gratuit et sans engagement</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#0A2320] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span>Professionnels qualifiés et expérimentés</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="font-semibold">50% de crédit d'impôt immédiat</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
